import { useCallback, useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { C } from "../lib/theme.js";
import { fmt, computeScore } from "../lib/compute.js";

// Reserved dataKey for the aggregated portfolio line.
const PORTFOLIO_CODE = "__portfolio";

// Distinct-ish palette cycled per property code. Works on the off-white
// background and stays legible with 17+ lines on the chart.
const PALETTE = [
  "#2C5545", "#B83A32", "#3A6B9F", "#9A7B1F", "#6A9A84",
  "#C15A8E", "#5C8A3A", "#A0522D", "#4A6FA5", "#7A3A9A",
  "#C49A1F", "#1F6B7A", "#8B4513", "#2D7A4F", "#D4756E",
  "#4B5A8F", "#8F5A4B", "#5A8F4B", "#8F4B5A", "#4B8F8F",
];

const colorFor = (code, i) => PALETTE[i % PALETTE.length];

// Each snapshot row is a full property-shaped object (see server/db.js
// rowToProperty). Pull the numeric value for the selected KPI; apply
// ownership weighting for revenue-ish KPIs when viewing the owner's share.
function valueFor(snap, kpi, ownerPct, { basis, benchmarks, weights }) {
  switch (kpi) {
    case "noi":
      return Math.round((snap.noi?.a || 0) * (ownerPct ?? 1));
    case "netIncome":
      return Math.round((snap.net_income?.a || 0) * (ownerPct ?? 1));
    case "capex":
      return Math.round((snap.capex?.a || 0) * (ownerPct ?? 1));
    case "occupancy": {
      const gr = snap.gross_rent?.a || 0;
      if (!gr) return 0;
      // deductions is derived server-side (net_rental - gross_rent). Rebuild
      // from what's on the snapshot so historical rows behave like current.
      const ded =
        snap.deductions?.a ??
        ((snap.net_rental?.a || 0) - gr);
      return +(100 * (1 - Math.abs(ded) / gr)).toFixed(2);
    }
    case "score": {
      const res = computeScore(snap, basis, benchmarks, weights);
      return res.composite;
    }
    default:
      return 0;
  }
}

// Roll up property values for a given month into a single portfolio number.
// - Dollar KPIs (noi / netIncome / capex): plain sum (ownership is already
//   applied inside valueFor for owner view).
// - Occupancy: gross-rent-weighted so small properties don't swamp the
//   average. Computed directly from the raw snapshots for that month.
// - Score: simple arithmetic mean of monthly composites, matching the
//   "Portfolio Score" / "Avg Score" header scorecard.
function aggregatePortfolio(kpi, perPropertyValues, snapsThisMonth) {
  const nums = perPropertyValues.filter((v) => typeof v === "number");
  if (nums.length === 0) return null;

  if (kpi === "noi" || kpi === "netIncome" || kpi === "capex") {
    return Math.round(nums.reduce((a, b) => a + b, 0));
  }

  if (kpi === "occupancy") {
    let grossSum = 0;
    let dedSum = 0;
    for (const snap of snapsThisMonth) {
      const gr = snap.gross_rent?.a || 0;
      if (!gr) continue;
      const ded =
        snap.deductions?.a ??
        ((snap.net_rental?.a || 0) - gr);
      grossSum += gr;
      dedSum += Math.abs(ded);
    }
    if (!grossSum) return null;
    return +(100 * (1 - dedSum / grossSum)).toFixed(2);
  }

  if (kpi === "score") {
    return +(nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(1);
  }

  return null;
}

const KPIS = [
  { id: "noi", label: "NOI", yFmt: fmt, tooltipSuffix: "" },
  { id: "netIncome", label: "Net Income", yFmt: fmt, tooltipSuffix: "" },
  // fitDomain: occupancy lives in a narrow band (typically 75-100%), so a
  // 0-based axis flattens every line. Fit the axis to the visible data.
  { id: "occupancy", label: "Occupancy %", yFmt: (v) => `${v.toFixed(0)}%`, tooltipSuffix: "%", fitDomain: true, max: 100 },
  { id: "capex", label: "CapEx (mo)", yFmt: fmt, tooltipSuffix: "" },
  { id: "score", label: "Score", yFmt: (v) => `${Math.round(v)}`, tooltipSuffix: "" },
];

// Build a tight [lo, hi] Y domain plus round tick values for a percentage
// KPI. Pads a little so points never sit on the axis edge, snaps to 5s (or
// 10s when the spread is wide), and never exceeds `max`.
export function fitPercentDomain(values, max = 100) {
  const nums = values.filter((v) => typeof v === "number" && Number.isFinite(v));
  if (nums.length === 0) return null;
  const min = Math.min(...nums);
  const mx = Math.max(...nums);
  let step = 5;
  let lo = Math.max(0, Math.floor((min - 2) / step) * step);
  let hi = Math.min(max, Math.ceil((mx + 2) / step) * step);
  if (hi - lo <= 0) hi = Math.min(max, lo + step);
  if ((hi - lo) / step > 8) {
    step = 10;
    lo = Math.max(0, Math.floor((min - 2) / step) * step);
    hi = Math.min(max, Math.ceil((mx + 2) / step) * step);
  }
  const ticks = [];
  for (let t = lo; t <= hi + 1e-9; t += step) ticks.push(t);
  return { domain: [lo, hi], ticks };
}

function formatPeriodShort(period) {
  if (!period || !/^\d{4}-\d{2}$/.test(period)) return period;
  const [y, m] = period.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(m, 10) - 1]} ${y.slice(2)}`;
}

// 12-month KPI trend chart. Defaults to a single aggregated Portfolio line;
// users opt in to individual property lines via the legend. Owner view
// weights dollar KPIs by ownership %.
export default function TrendChart({
  allSnapshots,
  properties,
  periods,
  period,
  isOwner,
  getPct,
  basis,
  benchmarks,
  weights,
  demo = false,
}) {
  const [kpi, setKpi] = useState("noi");
  const [hidden, setHidden] = useState({});

  const { rows, propsSorted, realProps } = useMemo(() => {
    const cutoffIdx = Math.max(0, periods.indexOf(period));
    // periods is DESC; slice forward 12 then reverse so chart reads left->right.
    const windowDesc = periods.slice(cutoffIdx, cutoffIdx + 12);
    const windowAsc = [...windowDesc].reverse();

    // Index all snapshots by (period, code) once.
    const byKey = new Map();
    for (const s of allSnapshots) {
      byKey.set(`${s.period}::${s.code}`, s);
    }

    const base = [...properties].sort((a, b) =>
      (a.alias || a.code).localeCompare(b.alias || b.code)
    );
    // In owner view, leave out unconfigured properties so the legend
    // isn't cluttered with lines that have no ownership weighting.
    const realProps = isOwner ? base.filter((p) => getPct(p) > 0) : base;
    // Portfolio line sits in front of the individual property lines.
    const propsSorted = [
      { code: PORTFOLIO_CODE, alias: "Total" },
      ...realProps,
    ];

    const kpiCfg = { basis, benchmarks, weights };
    const rows = windowAsc.map((per) => {
      const row = { period: per, label: formatPeriodShort(per) };
      const perProp = [];
      const snapsThisMonth = [];
      for (const p of realProps) {
        const snap = byKey.get(`${per}::${p.code}`);
        if (!snap) continue;
        const ownerPct = isOwner ? getPct(p) : 1;
        const v = valueFor(snap, kpi, ownerPct, kpiCfg);
        row[p.code] = v;
        perProp.push(v);
        snapsThisMonth.push(snap);
      }
      const portfolio = aggregatePortfolio(kpi, perProp, snapsThisMonth);
      if (portfolio !== null) row[PORTFOLIO_CODE] = portfolio;
      return row;
    });
    return { rows, propsSorted, realProps };
  }, [allSnapshots, periods, period, properties, isOwner, getPct, kpi, basis, benchmarks, weights]);

  const kpiMeta = KPIS.find((k) => k.id === kpi) || KPIS[0];

  const hasEnough = rows.filter((r) => typeof r[PORTFOLIO_CODE] === "number").length >= 2;

  // Y-axis range. Dollar KPIs keep Recharts' 0-based default; percentage
  // KPIs flagged fitDomain zoom to the lines currently shown so month-to-month
  // movement is legible. Recomputes as legend entries are toggled.
  const yFit = useMemo(() => {
    if (!kpiMeta.fitDomain) return null;
    const visible = propsSorted.filter((p) => !hidden[p.code]).map((p) => p.code);
    const values = [];
    for (const r of rows) for (const c of visible) values.push(r[c]);
    return fitPercentDomain(values, kpiMeta.max);
  }, [kpiMeta, propsSorted, hidden, rows]);

  // Default-hide every individual property on mount and whenever the set of
  // real properties changes (Owner/Total toggle, archive, etc.). Portfolio
  // always starts visible. Using a stable code-list key so swapping ownerPct
  // doesn't reshuffle visibility.
  const codeListKey = realProps.map((p) => p.code).join(",");
  useEffect(() => {
    setHidden(
      Object.fromEntries(realProps.map((p) => [p.code, true])),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeListKey]);

  const toggle = useCallback((code) => {
    setHidden((prev) => ({ ...prev, [code]: !prev[code] }));
  }, []);

  const showAll = () => setHidden({});
  const portfolioOnly = () =>
    setHidden(Object.fromEntries(realProps.map((p) => [p.code, true])));

  const strokeFor = (p, i) =>
    p.code === PORTFOLIO_CODE ? C.text : colorFor(p.code, i - 1);

  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: "12px 14px 8px",
        marginTop: 16,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
            12-Month Trend
          </div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>
            {isOwner ? (demo ? "Owner share · " : "RRC share · ") : ""}
            {kpiMeta.label} across the portfolio · click a legend entry to add a property
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 3 }}>
            {KPIS.map((k) => (
              <button
                key={k.id}
                onClick={() => setKpi(k.id)}
                style={{
                  background: kpi === k.id ? C.accent : "transparent",
                  color: kpi === k.id ? "#fff" : C.sub,
                  border: `1px solid ${kpi === k.id ? C.accent : C.border}`,
                  padding: "3px 10px",
                  borderRadius: 4,
                  fontSize: 11,
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                {k.label}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 3 }}>
            <button
              onClick={showAll}
              style={selectAllBtn}
              title="Show Total plus every property"
            >
              Show all
            </button>
            <button
              onClick={portfolioOnly}
              style={selectAllBtn}
              title="Reset to just the Total line"
            >
              Total Only
            </button>
          </div>
        </div>
      </div>

      {!hasEnough ? (
        <div
          style={{
            fontSize: 12,
            color: C.sub,
            padding: "24px 0",
            textAlign: "center",
          }}
        >
          Upload additional months to see trends.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={420}>
          <LineChart data={rows} margin={{ top: 5, right: 10, left: 5, bottom: 0 }}>
            <CartesianGrid stroke={C.borderLight} vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: C.sub }}
              axisLine={{ stroke: C.border }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: C.dim }}
              tickFormatter={kpiMeta.yFmt}
              axisLine={{ stroke: C.border }}
              tickLine={false}
              width={54}
              domain={yFit ? yFit.domain : [0, "auto"]}
              ticks={yFit ? yFit.ticks : undefined}
              allowDataOverflow={false}
            />
            <Tooltip
              formatter={(v, name) => {
                const p = propsSorted.find((x) => x.code === name);
                const label = p ? p.alias || p.code : name;
                if (kpi === "occupancy") return [`${Number(v).toFixed(1)}%`, label];
                if (kpi === "score") return [`${Math.round(v)}`, label];
                return [fmt(v), label];
              }}
              labelStyle={{ fontSize: 12, color: C.text }}
              contentStyle={{
                background: C.card,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                fontSize: 12,
              }}
            />
            <Legend
              iconType="plainline"
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              onClick={(o) => toggle(o.dataKey)}
              formatter={(value) => {
                const p = propsSorted.find((x) => x.code === value);
                const label = p ? p.alias || p.code : value;
                const isHidden = !!hidden[value];
                const isPortfolio = value === PORTFOLIO_CODE;
                return (
                  <span
                    style={{
                      color: isHidden ? C.dim : isPortfolio ? C.text : C.sub,
                      fontWeight: isPortfolio ? 600 : 400,
                      textDecoration: isHidden ? "line-through" : "none",
                      cursor: "pointer",
                    }}
                  >
                    {label}
                  </span>
                );
              }}
            />
            {propsSorted.map((p, i) => {
              const isPortfolio = p.code === PORTFOLIO_CODE;
              const color = strokeFor(p, i);
              return (
                <Line
                  key={p.code}
                  type="linear"
                  dataKey={p.code}
                  stroke={color}
                  strokeWidth={isPortfolio ? 2.75 : 1.75}
                  dot={{ r: isPortfolio ? 3 : 2.5, strokeWidth: 0, fill: color }}
                  activeDot={{ r: isPortfolio ? 5 : 4, strokeWidth: 0, fill: color }}
                  hide={!!hidden[p.code]}
                  connectNulls
                  isAnimationActive={false}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

const selectAllBtn = {
  background: "transparent",
  color: C.sub,
  border: `1px solid ${C.border}`,
  padding: "3px 10px",
  borderRadius: 4,
  fontSize: 11,
  cursor: "pointer",
  fontWeight: 500,
};
